import React, { useEffect } from 'react';
import { usePlanPrices } from '../../src/lib/planPricing';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, APP_VERSION, PUBLIC_PATHS_METADATA } from '../../constants';
import ogImageManifest from '../../src/content/ogImages.json';

const OG_IMAGES = ogImageManifest as Record<string, string>;

type StaticPage = 'landing' | 'deck' | 'privacy' | 'terms' | 'architect' | 'partners' | 'brand' | 'app' | 'plu' | 'units' | 'faq' | 'signin' | 'signup' | 'about' | 'features' | 'pricing';
type DiscoveryPage = 'prices' | 'cheapest' | 'history' | 'battle' | 'blog' | 'alternatives';

interface SEOProps {
  page: StaticPage | DiscoveryPage;
  subTitle?: string;
  description?: string;
  keywords?: string;
  image?: string;
  structuredData?: string;
  /** Discovery pages: true adds noindex. Left undefined, the server's robots decision stands. */
  noIndex?: boolean;
  context?: {
    product?: string;
    city?: string;
    storeA?: string;
    storeB?: string;
    brand?: string;
    gtin?: string;
    upc?: string;
    /** Feature pages: the page's name in the breadcrumb (omit on the /features hub). */
    crumb?: string;
    /** Real Lattice prices shown on the page; Product/Offer markup is only emitted when present. */
    offers?: { low: number; high: number; count: number };
  };
}

const PUBLISHED_DATE = '2026-01-15T00:00:00Z';
const getModifiedDate = () => new Date().toISOString();

const LOCALE_MAP: Record<string, string> = {
  en:'en_US', fr:'fr_FR', es:'es_ES', de:'de_DE', pt:'pt_BR',
  ja:'ja_JP', zh:'zh_CN', ru:'ru_RU', ar:'ar_SA', hi:'hi_IN', sw:'sw_KE'
};

const OG_TYPE_MAP: Record<string, string> = {
  landing:'website', prices:'product', history:'article', battle:'article',
  plu:'dataset', units:'dataset', deck:'article', brand:'article', faq: 'website',
  architect:'website', partners:'website', privacy:'website', terms:'website', cheapest:'website',
  blog:'article', alternatives:'article', signin:'website', signup:'website', about:'profile', features:'website', pricing:'website'
};

const IMAGE_MAP: Record<string, string> = {
  landing: 'landing.jpg',
  deck: 'deck.jpg',
  privacy: 'privacy.jpg',
  terms: 'terms.jpg',
  brand: 'brand.jpg',
  plu: 'plu.jpg',
  units: 'units.jpg',
  faq: 'faq.jpg',
  architect: 'architect.jpg',
  partners: 'partners.jpg',
  prices: 'prices.jpg',
  cheapest: 'cheapest.jpg',
  history: 'history.jpg',
  battle: 'battle.jpg',
  signin: 'landing.jpg',
  features: 'landing.jpg',
  pricing: 'landing.jpg',
  signup: 'landing.jpg'
};

/**
 * Open Graph image for a path: its own screenshot from ogImages.json
 * (scripts/capture_og_images.mjs; a feature page first, then the URL's section,
 * then the page type), else the older per-type image, else /og-image.jpg.
 * Same order as ogImageFor in api/index.js.
 */
const ogImageFor = (page: string, pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  const keys = [parts[0] === 'features' && parts[1] ? `features/${parts[1]}` : null, parts[0] || null, page];
  for (const key of keys) if (key && OG_IMAGES[key]) return OG_IMAGES[key];
  return IMAGE_MAP[page] || 'og-image.jpg';
};

const SEO: React.FC<SEOProps> = ({ page, subTitle, description, keywords, image, structuredData, noIndex, context = {} }) => {
  const { t, i18n } = useTranslation();
  const planPrices = usePlanPrices();
  const currentLang = i18n.language || 'en';

  // context.product is real product data (the full canonical nomenclature,
  // e.g. "Mucinex (Cold & Flu Relief, Dissolving Cold Remedies, 24ct)") — it
  // is never shortened. Only the hardcoded template text around it (in
  // src/i18n/locales/*/ui.json's seo.* strings) is kept short, so a long
  // product name pushes total length over the typical ~60/~160 char SEO
  // guideline rather than the product's own nomenclature being altered.
  const interpolate = (str: string) =>
    str
      .replace(/\{\{product\}\}/g, context.product || '')
      .replace(/\{\{city\}\}/g, context.city || '')
      .replace(/\{\{storeA\}\}/g, context.storeA || '')
      .replace(/\{\{storeB\}\}/g, context.storeB || '');

  const isDiscovery = ['prices', 'cheapest', 'history', 'battle', 'blog', 'alternatives'].includes(page);

  const usesOwnMeta = page === 'blog' || page === 'alternatives' || page === 'features' || page === 'pricing';
  const title = usesOwnMeta && subTitle
    ? subTitle
    : isDiscovery
    ? interpolate(t(`seo.${page}.title`, { defaultValue: 'CartLens Comparisons' }))
    : page === 'app'
    ? `${subTitle ? subTitle + ' | ' : ''}CartLens`
    : page === 'signin'
    ? t('seo.signin.title', { defaultValue: 'Access Your Spending DNA Node — Sign In | CartLens' })
    : page === 'signup'
    ? t('seo.signup.title', { defaultValue: 'Orchestrate Your Spending DNA — Create Account | CartLens' })
    : t(`seo.${page}.title`);

  const effectiveDescription = (usesOwnMeta && description)
    ? description
    : isDiscovery
    ? interpolate(t(`seo.${page}.description`, { defaultValue: '' }))
    : page === 'app'
    ? t('seo.landing.description')
    : page === 'signin'
    ? t('seo.signin.description', { defaultValue: 'Sign in to your CartLens account to scan receipts, check the Geospatial Lattice for prices near you, and manage your sovereign Spending DNA.' })
    : page === 'signup'
    ? t('seo.signup.description', { defaultValue: 'Create a free CartLens account. Join the sovereign price intelligence movement, scan receipts to verify prices, and access the Geospatial Lattice.' })
    : t(`seo.${page}.description`);

  const effectiveKeywords = (usesOwnMeta && keywords)
    ? keywords
    : isDiscovery
    ? interpolate(t(`seo.${page}.keywords`, { defaultValue: '' }))
    : page === 'signin'
    ? t('seo.signin.keywords', { defaultValue: 'CartLens sign in, access account, Spending DNA login, Geospatial Lattice account, sign in CartLens' })
    : page === 'signup'
    ? t('seo.signup.keywords', { defaultValue: 'CartLens sign up, create account, register CartLens, Spending DNA signup, join Geospatial Lattice' })
    : t(`seo.${page}.keywords`);

  const author = t(`seo.${page}.author`, { defaultValue: 'CartLens Intelligence' });

  const getSchemaMarkup = () => {
    const baseUrl = window.location.origin;
    const currentUrl = window.location.href;
    const modifiedDate = getModifiedDate();
    const imageUrl = `${baseUrl}/${ogImageFor(page, window.location.pathname)}`;

    const personId = "https://www.nzouat.com/#person";
    const productId = `${baseUrl}/#product`;

    // Nested (not hoisted to top-level @graph nodes) — nothing needs to
    // reference these from multiple places, so they live inline on whichever
    // entity owns them instead of appearing as standalone graph entries.
    const logoImage = {
      "@type": "ImageObject",
      "url": `${baseUrl}/logo.svg`,
      "contentUrl": `${baseUrl}/logo.svg`,
      "width": 512,
      "height": 512
    };

    const productImage = {
      "@type": "ImageObject",
      "url": `${baseUrl}/landing.jpg`,
      "contentUrl": `${baseUrl}/landing.jpg`,
      "caption": "CartLens AI-powered receipt scanning, local price comparison, and shopping intelligence platform"
    };

    const person = {
      "@type": "Person",
      "@id": personId,
      "name": "Moise Christian Nzouatoum",
      "alternateName": ["Christian M. Nzouat", "Christian Nzouat", "Chris Nzouat"],
      "url": "https://www.nzouat.com/",
      "image": {
        "@type": "ImageObject",
        "@id": "https://www.nzouat.com/#headshot",
        "url": "https://github.com/watat83.png",
        "contentUrl": "https://github.com/watat83.png",
        "name": "Moise Christian Nzouatoum",
        "caption": "Moise Christian Nzouatoum, founder of CartLens"
      },
      "jobTitle": ["Founder of CartLens", "Full-Stack Software Architect", "AI Developer"],
      "description": "Moise Christian Nzouatoum is a full-stack software architect, AI developer, and founder of CartLens. He develops AI-powered applications, autonomous agents, retail intelligence systems, data platforms, marketing automation systems, and digital products that connect technical innovation with practical business growth.",
      "homeLocation": { "@type": "Place", "name": "San Francisco Bay Area, California, United States" },
      "workLocation": { "@type": "Place", "name": "San Francisco Bay Area, California, United States" },
      "sameAs": [
        "https://www.linkedin.com/in/moise/",
        "https://github.com/watat83",
        "https://x.com/moise_chris",
        "https://orcid.org/0009-0008-7740-0498"
      ],
      "knowsAbout": [
        "Artificial Intelligence", "Agentic AI", "Generative AI", "Large Language Models",
        "Retrieval-Augmented Generation", "Machine Learning", "Computer Vision",
        "Optical Character Recognition", "Receipt OCR", "Retail Price Intelligence",
        "Price Comparison Technology", "Crowdsourced Pricing Data", "Shopping Analytics",
        "Consumer Price Transparency", "Full-Stack Web Development", "Software Architecture",
        "Progressive Web Applications", "API Development", "Marketing Automation",
        "Search Engine Optimization", "Blockchain Development", "Smart Contracts"
      ],
      "alumniOf": { "@type": "CollegeOrUniversity", "name": "Nova Southeastern University", "sameAs": "https://www.nova.edu/" },
      "hasCredential": [
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Master of Science in Management Information Systems",
          "credentialCategory": "Degree",
          "recognizedBy": { "@type": "CollegeOrUniversity", "name": "Nova Southeastern University", "sameAs": "https://www.nova.edu/" }
        },
        {
          "@type": "EducationalOccupationalCredential",
          "name": "Master of Business Administration in Entrepreneurship",
          "credentialCategory": "Degree",
          "recognizedBy": { "@type": "CollegeOrUniversity", "name": "Nova Southeastern University", "sameAs": "https://www.nova.edu/" }
        }
      ],
      "worksFor": { "@id": `${baseUrl}/#organization` },
      "owns": { "@id": productId }
    };

    const productCategory = [
      "AI Shopping Assistant",
      "Price Comparison Software",
      "Receipt Scanner",
      "Retail Price Intelligence",
      "Shopping Analytics Software"
    ];

    const productFeatureList = [
      "AI-powered receipt scanning",
      "Product price-tag scanning",
      "Receipt OCR and automatic digitization",
      "Local price comparison",
      "Crowdsourced retail pricing",
      "Fair-price checks before and after purchase",
      "Nearby price discovery",
      "Unit-price comparison",
      "Personalized spending insights",
      "Historical purchase analysis",
      "Conversational AI shopping assistant",
      "Master Insight spending audits"
    ];

    const productKeywords = [
      "AI shopping assistant", "receipt scanner", "receipt analysis", "receipt OCR",
      "price tracker", "price comparison", "local price comparison", "grocery savings",
      "shopping savings", "retail price intelligence", "crowdsourced pricing data",
      "shopping analytics", "consumer price transparency", "purchase analysis", "spending insights"
    ];

    const productOffers = [
      { "@type": "Offer", "@id": `${baseUrl}/#free-offer`, "name": "CartLens Free", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": `${baseUrl}/pricing`, "category": "Free", "description": "For trying CartLens: scan receipts and price tags, and keep up to 3 trips a month." },
      { "@type": "Offer", "@id": `${baseUrl}/#pro-offer`, "name": "CartLens Pro", "price": planPrices.pro.monthly.toFixed(2), "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": `${baseUrl}/pricing`, "category": "Pro", "description": "For regular shoppers: unlimited trips, full history, price trends, AI insights and bulk receipt import." },
      { "@type": "Offer", "@id": `${baseUrl}/#ultra-offer`, "name": "CartLens Ultra", "price": planPrices.ultra.monthly.toFixed(2), "priceCurrency": "USD", "availability": "https://schema.org/InStock", "url": `${baseUrl}/pricing`, "category": "Ultra", "description": "Everything in Pro plus CartAssistant AI chat, better-deal alerts, the local price map and store comparisons without credits." }
    ];

    const productCommonFields: any = {
      "@id": productId,
      "@type": "Product",
      "alternateName": "CartLens AI",
      "releaseDate": "2026-05-04",
      "applicationSubCategory": "Price Comparison and Shopping Intelligence",
      "brand": { "@id": `${baseUrl}/#organization` },
      "manufacturer": { "@id": `${baseUrl}/#organization` },
      "creator": { "@id": personId },
      "author": { "@id": personId },
      "publisher": { "@id": `${baseUrl}/#organization` },
      "category": productCategory,
      "audience": { "@type": "PeopleAudience", "audienceType": "Consumers and everyday shoppers" },
      "keywords": productKeywords,
      "sameAs": ["https://www.producthunt.com/products/cartlens"]
    };

    const productNode: any = {
      ...productCommonFields,
      "name": "CartLens",
      "url": baseUrl,
      "mainEntityOfPage": baseUrl,
      "applicationCategory": "ShoppingApplication",
      "operatingSystem": "Web, iOS, Android, PWA",
      "image": productImage,
      "description": "CartLens is an AI-powered shopping and retail price intelligence platform that scans price tags and receipts to show whether a price is fair, before or after purchase, compare real local prices, analyze spending, and discover better prices nearby.",
      "slogan": "Compare Prices Shared by Shoppers Near You, Before and After You Buy.",
      "featureList": productFeatureList,
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "USD",
        "lowPrice": "0.00",
        "highPrice": planPrices.ultra.monthly.toFixed(2),
        "offerCount": "3",
        "offers": productOffers
      }
    };

    const organization = {
      "@type": "Organization",
      "@id": `${baseUrl}/#organization`,
      "name": "CartLens",
      "legalName": "CartLens, Inc.",
      "url": baseUrl,
      "description": "CartLens is an AI-powered retail price intelligence company that helps consumers check prices before they buy, review what they paid, compare local prices, and make better shopping decisions.",
      "slogan": "Compare Prices Shared by Shoppers Near You, Before and After You Buy.",
      "founder": { "@id": personId },
      "employee": [{ "@id": personId }],
      "owns": { "@id": productId },
      "logo": logoImage,
      "image": productImage,
      "sameAs": [
        "https://x.com/CartLensApp",
        "https://www.reddit.com/r/CartLens/",
        "https://www.instagram.com/cartlensapp/",
        "https://www.linkedin.com/company/cartlens",
        "https://www.facebook.com/cartlens",
        "https://www.tiktok.com/@cartlens",
        "https://www.youtube.com/@cartlensapp",
        "https://www.producthunt.com/products/cartlens"
      ]
    };

    const breadcrumbItems: any[] = [
      { "@type": "ListItem", "position": 1, "name": "CartLens", "item": baseUrl }
    ];

    let pageSchema: any = {
      "@type": "WebPage",
      "name": title,
      "description": effectiveDescription,
      "url": currentUrl,
      "publisher": { "@id": `${baseUrl}/#organization` },
      "inLanguage": currentLang,
      "datePublished": PUBLISHED_DATE,
      "dateModified": modifiedDate,
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": imageUrl
      }
    };

    // Product markup from real Lattice prices only: no ratings or reviews (the
    // Lattice has none), and no Product node without prices to back it.
    const latticeProductSchema = () => context.offers ? {
      "@type": "ItemPage",
      "mainEntity": {
        "@type": "Product",
        "name": context.product || "Retail Product",
        "description": effectiveDescription,
        "image": imageUrl,
        ...(context.brand && { "brand": { "@type": "Brand", "name": context.brand } }),
        ...(context.gtin && { "gtin": context.gtin }),
        ...(context.upc && { "gtin12": context.upc }),
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": context.offers.low.toFixed(2),
          "highPrice": context.offers.high.toFixed(2),
          "offerCount": String(context.offers.count),
        },
      },
    } : { "@type": "WebPage" };

    if (page === 'prices') {
      const citySlugForCrumb = context.city
        ? context.city.toLowerCase().replace(/\s+/g, '-')
        : null;
      breadcrumbItems.push(
        {
          "@type": "ListItem", "position": 2,
          "name": context.city || "City",
          "item": citySlugForCrumb ? `${baseUrl}/stores/cheapest-in/${citySlugForCrumb}` : baseUrl
        },
        { "@type": "ListItem", "position": 3, "name": context.product || "Product", "item": currentUrl }
      );
      pageSchema = {
        ...pageSchema,
        ...latticeProductSchema(),
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] },
        "abstract": interpolate(t('seo.prices.schema.abstract')),
      };
    } else if (page === 'cheapest') {
      breadcrumbItems.push(
        { "@type": "ListItem", "position": 2, "name": context.city || "City", "item": currentUrl }
      );
      pageSchema = {
        ...pageSchema,
        "@type": "CollectionPage",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] },
        "abstract": interpolate(t('seo.cheapest.schema.abstract')),
        "mainEntity": {
          "@type": "ItemList",
          "name": `Cheapest Stores in ${context.city || 'your area'}`,
          "description": effectiveDescription,
          "image": imageUrl,
          "itemListOrder": "https://schema.org/ItemListOrderAscending",
        },
      };
    } else if (page === 'history') {
      breadcrumbItems.push(
        { "@type": "ListItem", "position": 2, "name": "Price History", "item": baseUrl + '/history' },
        { "@type": "ListItem", "position": 3, "name": context.product || "Product", "item": currentUrl }
      );
      pageSchema = {
        ...pageSchema,
        ...latticeProductSchema(),
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] },
      };
    } else if (page === 'battle') {
      const citySlugForCrumb = context.city
        ? context.city.toLowerCase().replace(/\s+/g, '-')
        : null;
      breadcrumbItems.push(
        {
          "@type": "ListItem", "position": 2,
          "name": context.city || "City",
          "item": citySlugForCrumb ? `${baseUrl}/stores/cheapest-in/${citySlugForCrumb}` : baseUrl
        },
        { "@type": "ListItem", "position": 3, "name": `${context.storeA} vs ${context.storeB}`, "item": currentUrl }
      );
      pageSchema = {
        ...pageSchema,
        "@type": "CollectionPage",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] },
        "abstract": interpolate(t('seo.battle.schema.abstract')),
        "mainEntity": {
          "@type": "ItemList",
          "name": `${context.storeA} vs ${context.storeB} Price Comparison in ${context.city || 'your area'}`,
          "description": effectiveDescription,
          "image": imageUrl,
          "itemListOrder": "https://schema.org/ItemListOrderDescending",
        },
      };
    } else if (page === 'landing') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Home", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        ...productCommonFields,
        "applicationCategory": "ShoppingApplication",
        "operatingSystem": "Web, iOS, Android, PWA",
        "abstract": t('seo.landing.schema.abstract'),
        "image": productImage,
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": "0.00",
          "highPrice": planPrices.ultra.monthly.toFixed(2),
          "offerCount": "3",
          "offers": productOffers
        },
        "featureList": productFeatureList,
      };
    } else if (page === 'about') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "About", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "AboutPage",
        "mainEntity": { "@id": `${baseUrl}/#organization` }
      };
    } else if (page === 'brand') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Brand", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "WebPage",
        "mainEntity": {
          "@type": "CreativeWork",
          "name": "CartLens Brand Assets & Guidelines",
          "description": "Official brand guidelines, logos, and design assets for CartLens.",
          "publisher": { "@id": `${baseUrl}/#organization` },
          "image": imageUrl
        }
      };
    } else if (page === 'deck') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Pitch Deck", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "PresentationDigitalDocument",
        "name": t('seo.deck.title'),
        "description": t('seo.deck.schema.abstract'),
        "image": imageUrl,
        "author": { "@id": `${baseUrl}/#organization` },
        "about": {
          ...productCommonFields,
          "name": "CartLens",
          "applicationCategory": "ShoppingApplication",
          "image": productImage,
          "offers": {
            "@type": "AggregateOffer",
            "priceCurrency": "USD",
            "lowPrice": "0.00",
            "highPrice": planPrices.ultra.monthly.toFixed(2),
            "offerCount": "3",
            "offers": productOffers
          },
          "featureList": productFeatureList
        },
        "hasPart": [
          { "@type": "WebPageElement", "name": t('seo.deck.schema.section_market') },
          { "@type": "WebPageElement", "name": t('seo.deck.schema.section_tech') },
          { "@type": "WebPageElement", "name": t('seo.deck.schema.section_growth') }
        ],
      };
    } else if (page === 'plu') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "PLU Registry", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "Dataset",
        "name": t('seo.plu.title'),
        "description": t('seo.plu.description'),
        "image": imageUrl,
        "license": "https://creativecommons.org/licenses/by/4.0/",
        "isAccessibleForFree": true,
        "creator": { "@id": `${baseUrl}/#organization` },
        "distribution": [{ "@type": "DataDownload", "encodingFormat": "application/json", "contentUrl": `${baseUrl}/api/plu?limit=100` }]
      };
    } else if (page === 'units') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Unit Tracking", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "Dataset",
        "name": t('seo.units.title'),
        "description": t('seo.units.description'),
        "image": imageUrl,
        "license": "https://creativecommons.org/licenses/by/4.0/",
        "isAccessibleForFree": true,
        "creator": { "@id": `${baseUrl}/#organization` },
        "distribution": [{ "@type": "DataDownload", "encodingFormat": "application/json", "contentUrl": `${baseUrl}/api/units?limit=100` }]
      };
    } else if (page === 'architect') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Lattice Architect", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "WebPage",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] },
        "mainEntity": {
          "@type": "Service",
          "name": "Lattice Architect Program",
          "description": t('seo.architect.description'),
          "image": imageUrl,
          "provider": { "@id": `${baseUrl}/#organization` },
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" }
        }
      };
    } else if (page === 'partners') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Merchant Partners", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "WebPage",
        "speakable": { "@type": "SpeakableSpecification", "cssSelector": ["h1", "h2"] },
        "mainEntity": {
          "@type": "Service",
          "name": "Merchant Node Activation",
          "description": t('seo.partners.description'),
          "image": imageUrl,
          "provider": { "@id": `${baseUrl}/#organization` }
        }
      };
    } else if (page === 'faq') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "FAQ", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": t('faq.q1_q'), "acceptedAnswer": { "@type": "Answer", "text": t('faq.q1_a') } },
          { "@type": "Question", "name": t('faq.q2_q'), "acceptedAnswer": { "@type": "Answer", "text": t('faq.q2_a') } },
          { "@type": "Question", "name": t('faq.q3_q'), "acceptedAnswer": { "@type": "Answer", "text": t('faq.q3_a') } },
          { "@type": "Question", "name": t('faq.q4_q'), "acceptedAnswer": { "@type": "Answer", "text": t('faq.q4_a') } },
          { "@type": "Question", "name": t('faq.q5_q'), "acceptedAnswer": { "@type": "Answer", "text": t('faq.q5_a') } },
          { "@type": "Question", "name": t('faq.q6_q'), "acceptedAnswer": { "@type": "Answer", "text": t('faq.q6_a') } }
        ]
      };
    } else if (page === 'privacy') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "WebPage",
        "mainEntity": {
          "@type": "DigitalDocument",
          "name": t('seo.privacy.title'),
          "description": t('seo.privacy.description'),
          "image": imageUrl,
          "about": { "@id": `${baseUrl}/#organization` }
        }
      };
    } else if (page === 'terms') {
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": "Terms of Service", "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "WebPage",
        "mainEntity": {
          "@type": "DigitalDocument",
          "name": t('seo.terms.title'),
          "description": t('seo.terms.description'),
          "image": imageUrl,
          "about": { "@id": `${baseUrl}/#organization` }
        }
      };
    } else if (page === 'blog' || page === 'alternatives') {
      const section = page === 'alternatives' ? 'Alternatives' : 'Blog';
      const sectionUrl = `${baseUrl}/${page}`;
      breadcrumbItems.push(
        { "@type": "ListItem", "position": 2, "name": section, "item": sectionUrl },
        { "@type": "ListItem", "position": 3, "name": title, "item": currentUrl }
      );
      pageSchema["@type"] = "CollectionPage";
      if (structuredData) {
        try {
            const parsed = JSON.parse(structuredData);
            const breadcrumb = { "@type": "BreadcrumbList", "itemListElement": breadcrumbItems };
            if (Array.isArray(parsed)) return [organization, ...parsed, breadcrumb];
            return [organization, parsed, breadcrumb];
        } catch (e) {}
      }
    } else if (page === 'features') {
      // /features hub and /features/<slug>: the page passes its own WebPage,
      // HowTo and FAQPage nodes (built from the copy it shows) as structuredData.
      const hubUrl = `${baseUrl}/features`;
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": t('features_pages.common.breadcrumb_features', { defaultValue: 'Features' }), "item": hubUrl });
      if (context.crumb) breadcrumbItems.push({ "@type": "ListItem", "position": 3, "name": context.crumb, "item": currentUrl.split('?')[0] });
      pageSchema = { ...pageSchema, "@type": context.crumb ? "WebPage" : "CollectionPage" };
      if (structuredData) {
        try {
          const parsed = JSON.parse(structuredData);
          const extra = (Array.isArray(parsed) ? parsed : [parsed]).filter((n: any) => n?.['@type'] !== 'WebPage');
          return [organization, pageSchema, ...extra, { "@type": "BreadcrumbList", "itemListElement": breadcrumbItems }];
        } catch (e) {}
      }
    } else if (page === 'pricing') {
      // /pricing: the page passes its Product/Offer and FAQPage nodes,
      // built from the Clerk plans it shows, as structuredData.
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": t('pricing_page.breadcrumb', { defaultValue: 'Pricing' }), "item": currentUrl.split('?')[0] });
      if (structuredData) {
        try {
          const parsed = JSON.parse(structuredData);
          return [organization, pageSchema, ...(Array.isArray(parsed) ? parsed : [parsed]), { "@type": "BreadcrumbList", "itemListElement": breadcrumbItems }];
        } catch (e) {}
      }
    } else if (page === 'signin' || page === 'signup') {
      const isSignIn = page === 'signin';
      breadcrumbItems.push({ "@type": "ListItem", "position": 2, "name": isSignIn ? t('auth.sign_in', { defaultValue: 'Sign In' }) : t('auth.create_account', { defaultValue: 'Sign Up' }), "item": currentUrl });
      pageSchema = {
        ...pageSchema,
        "@type": "WebPage",
        "mainEntity": {
          "@type": "WebPage",
          "name": title,
          "description": effectiveDescription,
          "image": imageUrl,
          "about": { "@id": `${baseUrl}/#organization` }
        }
      };
    } else {
      breadcrumbItems.push({
        "@type": "ListItem", "position": 2,
        "name": page.charAt(0).toUpperCase() + page.slice(1),
        "item": currentUrl
      });
    }

    // Person/Product are only injected on pages where they're actually the
    // subject — avoids stuffing unrelated pages (privacy, faq, discovery, ...)
    // with structured data that isn't relevant to that page's content.
    const isEntityHubPage = ['landing', 'about', 'deck', 'brand'].includes(page);
    const graph: any[] = [organization];
    if (isEntityHubPage) {
      graph.push(person);
      if (page !== 'landing' && page !== 'deck') {
        graph.push(productNode);
      }
    }
    graph.push(
      pageSchema,
      { "@type": "BreadcrumbList", "itemListElement": breadcrumbItems }
    );
    if (page === 'landing') {
      // Site navigation is identical on every page — only the homepage needs
      // to carry it, rather than repeating the same unchanging list everywhere.
      const siteNavigationElements = PUBLIC_PATHS_METADATA
        .filter(m => m.isInSiteNavigation)
        .map((m, index) => {
          const cleanPath = m.path.replace('/', '').split('/')[0].split('#')[0];
          const pageImage = ogImageFor(m.path === '/' || m.isAnchor ? 'landing' : cleanPath, m.path.split('#')[0]);
          return {
            "@type": "SiteNavigationElement",
            "position": index + 1,
            "name": t(m.translationKey),
            "url": m.path === '/' ? baseUrl : `${baseUrl}${m.path}`,
            "thumbnailUrl": `${baseUrl}/${pageImage}`
          };
        });
      graph.push({
        "@type": "ItemList",
        "@id": `${baseUrl}/#site-navigation-list`,
        "name": "Site Navigation",
        "itemListElement": siteNavigationElements
      });
      graph.push({
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        "name": "CartLens",
        "alternateName": ["Cart Lens", "CartLens App", "Cart-Lens"],
        "url": baseUrl,
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${baseUrl}/?search={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      });
    }
    if (page === 'units') {
      graph.push({
        "@type": "FAQPage",
        "mainEntity": [1, 2, 3, 4, 5, 6].map(n => ({
          "@type": "Question",
          "name": t(`units.faq_q${n}_q`),
          "acceptedAnswer": { "@type": "Answer", "text": t(`units.faq_q${n}_a`) }
        }))
      });
      graph.push({
        "@type": "HowTo",
        "name": t('units.content_how_to_title'),
        "step": [1, 2, 3].map(n => ({
          "@type": "HowToStep",
          "name": t(`units.howto_step${n}_name`),
          "text": t(`units.howto_step${n}_text`)
        }))
      });
    }

    return graph;
  };

  useEffect(() => {
    const modifiedDate = getModifiedDate();
    const baseUrl = window.location.origin;
    const pageImage = image || ogImageFor(page, window.location.pathname);
    const imageUrl = pageImage.startsWith('http') || pageImage.startsWith('data:') ? pageImage : `${baseUrl}/${pageImage}`;

    // ── Helpers ──────────────────────────────────────────────────────────────
    // Removes ALL existing matching tags, then inserts one fresh element.
    // This prevents duplicate accumulation across language switches and navigation.
    const setMeta = (attrKey: string, attrVal: string, content: string) => {
      document.querySelectorAll(`meta[${attrKey}="${attrVal}"]`).forEach(el => el.remove());
      const meta = document.createElement('meta');
      meta.setAttribute(attrKey, attrVal);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    };

    const setLink = (rel: string, href: string, extra?: Record<string, string>) => {
      const selector = extra
        ? `link[rel="${rel}"]${Object.entries(extra).map(([k, v]) => `[${k}="${v}"]`).join('')}`
        : `link[rel="${rel}"]:not([hreflang])`;
      document.querySelectorAll(selector).forEach(el => el.remove());
      const link = document.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      if (extra) Object.entries(extra).forEach(([k, v]) => link.setAttribute(k, v));
      document.head.appendChild(link);
    };

    // ── Title (deduplicate <title> tags) ────────────────────────────────────
    document.querySelectorAll('title').forEach(el => el.remove());
    const titleEl = document.createElement('title');
    titleEl.textContent = title;
    document.head.appendChild(titleEl);
    document.documentElement.lang = currentLang;

    const ogLocale = LOCALE_MAP[currentLang] || `${currentLang}_${currentLang.toUpperCase()}`;
    const ogType = OG_TYPE_MAP[page] || 'website';

    // ── Primary meta ────────────────────────────────────────────────────────
    setMeta('name', 'description', effectiveDescription);
    setMeta('name', 'keywords', effectiveKeywords);
    setMeta('name', 'author', author);
    setMeta('name', 'title', title);
    setMeta('name', 'copyright', `© ${new Date().getFullYear()} CartLens`);
    const isDiscoveryPage = page === 'prices' || page === 'history' || page === 'cheapest' || page === 'battle';
    if (noIndex) setMeta('name', 'robots', 'noindex, follow');
    else if (!isDiscoveryPage) setMeta('name', 'robots', 'index, follow');
    setMeta('name', 'application-name', 'CartLens');

    const cleanPath = window.location.pathname;
    const canonicalHref = currentLang === 'en' 
      ? `${baseUrl}${cleanPath}` 
      : `${baseUrl}${cleanPath}?lng=${currentLang}`;

    // ── Open Graph ──────────────────────────────────────────────────────────
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', effectiveDescription);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:locale', ogLocale);
    setMeta('property', 'og:site_name', 'CartLens');
    setMeta('property', 'og:url', canonicalHref);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:image:alt', title);
    setMeta('property', 'article:published_time', PUBLISHED_DATE);
    setMeta('property', 'article:modified_time', modifiedDate);

    // ── Twitter ─────────────────────────────────────────────────────────────
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:site', '@cartlensApp');
    setMeta('name', 'twitter:creator', '@cartlensApp');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', effectiveDescription);
    setMeta('name', 'twitter:image', imageUrl);
    setMeta('name', 'twitter:image:alt', title);
    setMeta('name', 'twitter:url', canonicalHref);

    // ── Schema.org LD+JSON ──────────────────────────────────────────────────
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());
    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');

    const graph = getSchemaMarkup();

    // Dynamically detect video element or iframe inside DOM
    const videoNode = document.querySelector('.blog-content video');
    let pageVideo = null;
    if (videoNode) {
      const src = videoNode.getAttribute('src') || videoNode.querySelector('source')?.getAttribute('src');
      if (src) pageVideo = { url: src, isEmbed: false };
    }
    if (!pageVideo) {
      const iframeNode = document.querySelector('.blog-content iframe');
      if (iframeNode) {
        const src = iframeNode.getAttribute('src');
        if (src && (src.includes('youtube.com') || src.includes('youtu.be') || src.includes('vimeo.com') || src.includes('player.vimeo.com'))) {
          pageVideo = { url: src, isEmbed: true };
        }
      }
    }

    if (pageVideo) {
      const mainSchema = graph.find((item: any) => ['BlogPosting', 'NewsArticle', 'ItemPage', 'WebPage'].includes(item?.['@type']));
      if (mainSchema && !mainSchema.video) {
        mainSchema.video = {
          "@type": "VideoObject",
          "name": subTitle || title || mainSchema.name || "CartLens Media",
          "description": description || mainSchema.description || "",
          "thumbnailUrl": [image || mainSchema.image || `${window.location.origin}/og-image.jpg`],
          "uploadDate": PUBLISHED_DATE,
          [pageVideo.isEmbed ? "embedUrl" : "contentUrl"]: pageVideo.url
        };
      }
    }

    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    // ── Canonical ───────────────────────────────────────────────────────────
    setLink('canonical', canonicalHref);

    // ── Hreflang alternates ─────────────────────────────────────────────────
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    // x-default points to the canonical English URL
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', `${baseUrl}${window.location.pathname}`);
    document.head.appendChild(xDefault);
    LANGUAGES.forEach(lang => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang.code);
      link.setAttribute('href', `${baseUrl}${window.location.pathname}?lng=${lang.code}`);
      document.head.appendChild(link);
    });

    // ── RSS autodiscovery (English-only feeds; blog/newsroom/alternatives only) ──
    document.querySelectorAll('link[rel="alternate"][type="application/rss+xml"]').forEach(el => el.remove());
    const rssSection = cleanPath === '/blog' || cleanPath.startsWith('/blog/') ? { title: 'The CartLens Blog', path: '/blog' }
      : cleanPath === '/newsroom' || cleanPath.startsWith('/newsroom/') ? { title: 'CartLens Newsroom', path: '/newsroom' }
      : cleanPath === '/alternatives' || cleanPath.startsWith('/alternatives/') ? { title: 'CartLens Alternatives', path: '/alternatives' }
      : null;
    if (rssSection) {
      const rss = document.createElement('link');
      rss.setAttribute('rel', 'alternate');
      rss.setAttribute('type', 'application/rss+xml');
      rss.setAttribute('title', rssSection.title);
      rss.setAttribute('href', `${baseUrl}${rssSection.path}/rss.xml`);
      document.head.appendChild(rss);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- context compared by value
  }, [title, effectiveDescription, effectiveKeywords, author, currentLang, page, image, structuredData, noIndex, JSON.stringify(context), planPrices]);

  return null;
};

export default SEO;
